import React from 'react';
import moment from 'moment';
import { humanizeSnakeCase } from '../helpers';

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const formatClassifierValue = (value) => {
  if (value === null || value === undefined || value === '') return 'Unknown';
  return humanizeSnakeCase(value);
};

// Maps sortable column keys to the backend sort param accepted by
// POST /api/v1/maira_analytics. The Outcome column renders the display label
// but sorts by the raw stored call_outcome value.
const SORTABLE_COLUMN_SORT_KEYS = {
  started_at: 'started_at',
  duration_seconds: 'duration_seconds',
  sentiment_score: 'sentiment_score',
  call_outcome_display_label: 'call_outcome',
  department_name: 'department_name',
};

const ANTD_SORT_ORDERS = {
  asc: 'ascend',
  desc: 'descend',
};

const reportSortKeyForColumn = (columnKey) => SORTABLE_COLUMN_SORT_KEYS[columnKey] || null;

const sortProps = (columnKey, { sortBy, sortDirection, sortingEnabled }) => {
  const sortKey = SORTABLE_COLUMN_SORT_KEYS[columnKey];
  if (!sortKey || !sortingEnabled) return {};

  return {
    sorter: true,
    sortOrder: sortBy === sortKey ? (ANTD_SORT_ORDERS[sortDirection] || null) : null,
  };
};

// ENG-2926: the transfer columns appear only when the maira_transfer_breakdown
// flag is on (they are not sortable).
const TRANSFER_BREAKDOWN_COLUMN_KEYS = ['transfer_destination_category', 'caller_objective'];

const buildMairaReportColumns = (sortState = {}, { transferBreakdownEnabled = false } = {}) => [
  {
    title: 'Started At (Local)',
    dataIndex: 'started_at',
    key: 'started_at',
    fixed: 'left',
    width: 170,
    render: (value) => (value ? moment(value).format('MM/DD/YYYY HH:mm') : '-'),
    ...sortProps('started_at', sortState),
  },
  {
    title: 'Voice AI Number',
    dataIndex: 'voice_ai_number',
    key: 'voice_ai_number',
    width: 150,
    render: formatValue,
  },
  {
    title: 'Department',
    dataIndex: 'department_name',
    key: 'department_name',
    width: 160,
    render: formatValue,
    ...sortProps('department_name', sortState),
  },
  {
    title: 'Duration',
    dataIndex: 'duration_seconds',
    key: 'duration_seconds',
    width: 110,
    render: (value) => (value === null || value === undefined ? '-' : `${value}s`),
    ...sortProps('duration_seconds', sortState),
  },
  {
    title: 'Outcome',
    dataIndex: 'call_outcome_display_label',
    key: 'call_outcome_display_label',
    width: 230,
    render: formatValue,
    ...sortProps('call_outcome_display_label', sortState),
  },
  {
    title: 'Transfer Destination',
    dataIndex: 'transfer_destination_category',
    key: 'transfer_destination_category',
    width: 170,
    render: formatClassifierValue,
  },
  {
    title: 'Caller Objective',
    dataIndex: 'caller_objective',
    key: 'caller_objective',
    width: 170,
    render: formatClassifierValue,
  },
  {
    title: 'Incomplete',
    dataIndex: 'is_incomplete',
    key: 'is_incomplete',
    width: 120,
    render: (value) => {
      if (value === true) return 'Yes';
      if (value === false) return 'No';
      return '-';
    },
  },
  {
    title: 'Sentiment',
    dataIndex: 'sentiment_score',
    key: 'sentiment_score',
    width: 110,
    render: formatValue,
    ...sortProps('sentiment_score', sortState),
  },
  {
    title: 'Disconnection',
    dataIndex: 'disconnection_reason',
    key: 'disconnection_reason',
    width: 170,
    render: formatValue,
  },
  {
    title: 'Summary',
    dataIndex: 'call_summary',
    key: 'call_summary',
    width: 340,
    render: (value) => (
      <span className="maira-report-summary">
        {formatValue(value)}
      </span>
    ),
  },
].filter((column) => transferBreakdownEnabled || !TRANSFER_BREAKDOWN_COLUMN_KEYS.includes(column.key));

export { reportSortKeyForColumn };
export default buildMairaReportColumns;
